import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import BackButton from "../../../components/Button/BackButton";

const fmtNum = (v) =>
  v !== undefined && v !== null && v !== "" ? Number(v).toFixed(2) : "—";

const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const Row = ({ label, value, children }) => (
  <div className="flex border-b last:border-b-0 text-sm">
    <div className="w-1/3 bg-gray-50 px-4 py-3 font-medium text-gray-700">{label}</div>
    <div className="w-2/3 px-4 py-3 text-gray-900">{children ?? value ?? "—"}</div>
  </div>
);

const SectionCard = ({ title, children }) => (
  <div className="bg-white shadow rounded-lg overflow-hidden">
    <div className="px-6 py-4 border-b">
      <h3 className="text-sm font-semibold text-black-600 tracking-wide">{title}</h3>
    </div>
    <div>{children}</div>
  </div>
);

const COVERAGE_CLS = {
  "Fully covered":     "text-green-600 font-medium",
  "Partially covered": "text-amber-600 font-medium",
  "Not covered":       "text-red-500 font-medium",
};

const AdminVendorPaymentDetailPage = () => {
  const { id } = useParams();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [successMsg, setSuccessMsg] = useState(
    sessionStorage.getItem("paymentSuccessMsg") || ""
  );

  useEffect(() => {
    sessionStorage.removeItem("paymentSuccessMsg");
    api.get(`/project-finances/vendor-payments/${id}`, { withCredentials: true })
      .then((res) => setPayment(res.data.data))
      .catch(() => setError("Failed to fetch payment details."))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="flex justify-center items-center mt-16">
      <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-blue-600" />
    </div>
  );
  if (error) return <div className="text-center mt-10 text-red-600 font-medium">{error}</div>;
  if (!payment) return <div className="text-center mt-10 text-gray-500">Payment not found.</div>;

  const pm = payment.paymentMethod;
  const pmType = pm?.payment_method?.replace(/_/g, " ") || "—";

  let pmName = "—";
  let pmDetails = "—";
  if (pm?.bank_transfer_detail) {
    const bt = pm.bank_transfer_detail;
    pmName = bt.payment_method_name || bt.bank_name || "—";
    const parts = [];
    if (bt.bank_name) parts.push(`Bank name: ${bt.bank_name}`);
    if (bt.bank_address) parts.push(`Bank address: ${bt.bank_address}`);
    if (bt.country) parts.push(bt.country);
    pmDetails = parts.join("\n") || "—";
  } else if (pm?.email_payment_detail) {
    pmName = pm.email_payment_detail.email || "—";
    pmDetails = pm.email_payment_detail.email || "—";
  }

  const invoice = payment.linkedInvoice;
  const coverageCls = COVERAGE_CLS[payment.coverage_by_invoices] ?? "text-gray-600";
  const totalAfterWriteOff = invoice
    ? Number(invoice.total || 0) - Number(invoice.write_off_amount || 0)
    : null;

  return (
    <div className="space-y-6">
      {successMsg && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex justify-between items-center text-sm text-green-800">
          <span>{successMsg}</span>
          <button onClick={() => setSuccessMsg("")} className="text-green-600 hover:text-green-800 font-bold">✕</button>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <BackButton />
          <h2 className="text-2xl font-bold text-black-600">Payment {payment.payment_code}</h2>
        </div>
        <div className="flex items-center gap-2">
          <button className="bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700 text-sm font-medium">
            Download as PDF
          </button>
          <button className="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600 text-sm font-medium">
            Update
          </button>
          <button className="bg-red-500 text-white px-4 py-2 rounded shadow hover:bg-red-600 text-sm font-medium">
            Delete
          </button>
        </div>
      </div>

      {/* Main 2-col layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Left — payment details */}
        <SectionCard title="">
          <Row label="Vendor">
            {payment.vendor_id ? (
              <Link to={`/vendors/${payment.vendor_id}`} className="text-blue-600 hover:underline">
                {payment.vendorName}
              </Link>
            ) : payment.vendorName}
          </Row>
          <Row label="Amount">
            <strong>{fmtNum(payment.amount)}</strong>
          </Row>
          <Row label="Currency" value={payment.currency || "—"} />
          <Row label="Paid at" value={fmtDate(payment.paid_at)} />

          <div className="px-4 py-3 border-b">
            <p className="text-sm font-semibold text-black-600 tracking-wide">Coverage by invoices</p>
          </div>
          <Row label="Coverage by invoices">
            <span className={coverageCls}>{payment.coverage_by_invoices || "—"}</span>
          </Row>

          <div className="px-4 py-3 border-b">
            <p className="text-sm font-semibold text-black-600 tracking-wide">Other details</p>
          </div>
          <Row label="Legal entity" value={payment.vendor?.legal_entity || "—"} />
          <Row label="Note" value={payment.note || "—"} />
          <Row label="Created at">
            {fmtDate(payment.createdAt)}
            {payment.created_by ? <span className="ml-2 text-gray-500">| {payment.created_by}</span> : null}
          </Row>
        </SectionCard>

        {/* Right — Payment Method */}
        <SectionCard title="Payment Method">
          <Row label="Name" value={pmName} />
          <Row label="Type">
            <span className="capitalize">{pmType}</span>
          </Row>
          <Row label="Details">
            <span className="whitespace-pre-line">{pmDetails}</span>
          </Row>
        </SectionCard>
      </div>

      {/* Linked Invoices */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="text-sm font-semibold text-black-600 tracking-wide">Linked Invoices</h3>
        </div>
        {invoice && <p className="px-6 py-2 text-sm text-gray-600">Total <strong>1</strong> item.</p>}
        <div className="overflow-x-auto">
          <table className="w-full table-auto border-collapse text-xs min-w-[900px]">
            <thead>
              <tr className="bg-gray-50 border-b text-[11px] tracking-widest text-gray-500 uppercase">
                {["Code","Linked payments","Coverage by payments","Total after write off","Amount covered by linked payments","Amount covered by this payment","Amount due","Issued at",""].map((h) => (
                  <th key={h} className="px-3 py-2 font-semibold text-center whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoice ? (
                <tr className="border-b hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap">
                    <Link to={`/vendor/invoice/${invoice.id}`} className="text-blue-600 hover:underline font-medium">
                      {invoice.invoice_code}
                    </Link>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-center">{payment.payment_code}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={COVERAGE_CLS[payment.coverage_by_invoices] ?? "text-gray-600"}>
                      {payment.coverage_by_invoices || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{fmtNum(totalAfterWriteOff)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{fmtNum(invoice.paid_amount)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{fmtNum(payment.amount)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{fmtNum(invoice.due_amount)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDate(invoice.issued_at)}</td>
                  <td className="px-3 py-2 text-center">
                    <button className="bg-red-500 text-white px-2 py-0.5 rounded text-xs hover:bg-red-600">
                      Unlink
                    </button>
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-gray-400">No linked invoices.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Messages */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="text-sm font-semibold text-black-600 tracking-wide">Messages</h3>
        </div>
        <div className="px-6 py-4 space-y-3">
          <textarea
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full border border-gray-300 rounded p-3 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
          <button
            disabled={!message.trim()}
            className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Send message to vendor
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminVendorPaymentDetailPage;
